import { AnimatePresence, motion } from 'framer-motion';
import {
  IS_PRO,
  REGION,
  RegionEnum,
} from 'gatsby-theme-hem-store/src/constants';
import { LoadingIndicator, transition } from 'hem-ui';
import isFunction from 'lodash/isFunction';
import React, { Suspense } from 'react';

import { NewsletterForm } from '../NewsletterForm';

const SmsForm = React.lazy(() => import('../SmsForm'));

type Props = {
  form: 'newsletter' | 'sms' | 'both';
  newsletterTopTitle?: string;
  newsletterTitle?: string;
  newsletterContent?: string;
  smsTopTitle?: string;
  smsTitle?: string;
  smsContent?: string;
  onNewsletterSubmitSuccess?: () => void;
  tags?: string[];
  location?: string;
};

export const MarketingCommunicationForms: React.FC<Props> = ({
  form = 'newsletter',
  newsletterTopTitle,
  newsletterTitle,
  newsletterContent,
  smsTopTitle,
  smsTitle,
  smsContent,
  onNewsletterSubmitSuccess,
  tags,
  location,
}): JSX.Element => {
  const [step, setStep] = React.useState<'newsletter' | 'sms'>('newsletter');
  const [email, setEmail] = React.useState<string | undefined>(undefined);

  if (
    form === 'newsletter' ||
    REGION === RegionEnum.Eu ||
    REGION === RegionEnum.Gb ||
    IS_PRO
  ) {
    return (
      <>
        <div className="mb-8 space-y-2">
          {newsletterTopTitle ? (
            <p
              className="text-sm font-bold uppercase text-black-400"
              dangerouslySetInnerHTML={{ __html: newsletterTopTitle }}
            />
          ) : null}

          {newsletterTitle ? (
            <p
              className="text-black mb-2 text-balance text-lg font-bold"
              dangerouslySetInnerHTML={{ __html: newsletterTitle }}
            />
          ) : null}

          {newsletterContent ? (
            <p
              className="max-w-prose text-pretty text-base text-black-600"
              dangerouslySetInnerHTML={{ __html: newsletterContent }}
            />
          ) : null}
        </div>

        <NewsletterForm
          tags={tags}
          location={location}
          reportEmail={setEmail}
        />
      </>
    );
  }

  if (form === 'sms') {
    return (
      <Suspense fallback={<LoadingIndicator />}>
        <div>
          {smsTopTitle ? (
            <p
              className="text-sm font-bold uppercase text-black-400"
              dangerouslySetInnerHTML={{ __html: smsTopTitle }}
            />
          ) : null}

          {smsTitle ? (
            <p
              className="text-black mb-2 text-balance text-lg font-bold"
              dangerouslySetInnerHTML={{ __html: smsTitle }}
            />
          ) : null}

          {smsContent ? (
            <p
              className="max-w-prose text-pretty text-base text-black-600"
              dangerouslySetInnerHTML={{ __html: smsContent }}
            />
          ) : null}

          <div className="mb-8" />

          <SmsForm tags={tags} email={email} location={location} />
        </div>
      </Suspense>
    );
  }

  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.div
        key={step}
        initial={{ x: 20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: -20, opacity: 0 }}
        {...{ transition }}
      >
        {step === 'newsletter' ? (
          <>
            {newsletterTopTitle ? (
              <p
                className="text-sm font-bold uppercase text-black-400"
                dangerouslySetInnerHTML={{ __html: newsletterTopTitle }}
              />
            ) : null}

            {newsletterTitle ? (
              <p
                className="text-black mb-2 text-balance text-lg font-bold"
                dangerouslySetInnerHTML={{ __html: newsletterTitle }}
              />
            ) : null}

            {newsletterContent ? (
              <p
                className="text-pretty text-base text-black-600"
                dangerouslySetInnerHTML={{ __html: newsletterContent }}
              />
            ) : null}

            <div className="mb-8" />

            <NewsletterForm
              buttonLabel="Continue"
              onSuccess={() => {
                if (isFunction(onNewsletterSubmitSuccess)) {
                  onNewsletterSubmitSuccess();
                }
                setStep('sms');
              }}
              tags={tags}
              location={location}
              reportEmail={setEmail}
            />
          </>
        ) : null}
        {step === 'sms' ? (
          <>
            <Suspense fallback={<LoadingIndicator />}>
              {smsTopTitle ? (
                <p
                  className="text-sm font-bold uppercase text-black-400"
                  dangerouslySetInnerHTML={{ __html: smsTopTitle }}
                />
              ) : null}

              {smsTitle ? (
                <p
                  className="text-black mb-2 text-balance text-lg font-bold"
                  dangerouslySetInnerHTML={{ __html: smsTitle }}
                />
              ) : null}

              {smsContent ? (
                <p
                  className="text-pretty text-base text-black-600"
                  dangerouslySetInnerHTML={{ __html: smsContent }}
                />
              ) : null}

              <div className="mb-8" />

              <SmsForm tags={tags} location={location} email={email} />
            </Suspense>
          </>
        ) : null}
      </motion.div>
    </AnimatePresence>
  );
};
